import { html, type TemplateResult } from 'lit-html';
import { app } from './app.js';

export interface Column<T> {
  key: string;
  label: string;
  num?: boolean;
  /** Explains the column (header tooltip). */
  title?: string;
  /** Sort value; null sorts last either way. */
  value: (r: T) => number | string | null;
  cell: (r: T) => unknown;
}

export interface SortState {
  key: string;
  desc: boolean;
}

export function sortRows<T>(rows: T[], cols: Column<T>[], s: SortState): T[] {
  const col = cols.find((c) => c.key === s.key);
  if (!col) return rows;
  const dir = s.desc ? -1 : 1;
  return [...rows].sort((x, y) => {
    const a = col.value(x);
    const b = col.value(y);
    if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1;
    if (typeof a === 'number' && typeof b === 'number') return (a - b) * dir;
    return String(a).localeCompare(String(b)) * dir;
  });
}

/** Header cells that sort the table: numbers start high-to-low, text A–Z; a second click flips. */
export function sortHeader<T>(cols: Column<T>[], s: SortState): TemplateResult {
  const click = (c: Column<T>) => {
    if (s.key === c.key) s.desc = !s.desc;
    else Object.assign(s, { key: c.key, desc: !!c.num });
    app.rerender();
  };
  return html`<tr>${cols.map((c) => {
    const active = s.key === c.key;
    return html`<th class=${c.num ? 'num' : ''} aria-sort=${active ? (s.desc ? 'descending' : 'ascending') : 'none'} title=${c.title ?? ''}>
      <button type="button" class="sort ${active ? 'active' : ''}" @click=${() => click(c)}>${c.label}<span class="sort-mark" aria-hidden="true">${active ? (s.desc ? '▼' : '▲') : ''}</span></button></th>`;
  })}</tr>`;
}
